import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, X, Trash2, Save, PackageCheck, FileUp } from "lucide-react";
import { SkuAutocomplete } from "../components/SkuAutocomplete";

const BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${BASE}/api/compensations/receptions`;
const STATUS = {
  in_receptie: { label: "În recepție", cls: "bg-sky-500/15 text-sky-200" },
  verificare: { label: "Se verifică", cls: "bg-zinc-700 text-zinc-200" },
  de_revizuit: { label: "De revizuit", cls: "bg-rose-500/15 text-rose-200" },
  aprobat: { label: "Aprobat", cls: "bg-emerald-500/15 text-emerald-200" },
};
const errMsg = (e) => e.response?.data?.detail || e.message;

export default function ReceptionsTab() {
  const [items, setItems] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [parsing, setParsing] = useState(false);

  const importPdf = async (file) => {
    setParsing(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await axios.post(`${API}/parse-pdf`, fd);
      toast.success(`Cerere ${r.data.request_number}: ${r.data.items.length} SKU-uri citite — verifică și salvează`);
      setEditing({ draft: r.data });
    } catch (e) { toast.error("Import PDF: " + errMsg(e)); }
    finally { setParsing(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [r, pc] = await Promise.all([
        axios.get(API),
        axios.get(`${BASE}/api/products/catalog`).catch(() => ({ data: { items: [] } })),
      ]);
      setItems(r.data?.items || []);
      setCatalog((pc.data?.items || []).map((c) => ({ sku: c.sku, name: c.name || "" }))
        .sort((a, b) => a.sku.localeCompare(b.sku)));
    } catch (e) { toast.error("Eroare: " + errMsg(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Sigur ștergi această cerere de recepție?")) return;
    try { await axios.delete(`${API}/${id}`); toast.success("Șters"); load(); }
    catch (e) { toast.error("Eroare: " + errMsg(e)); }
  };

  return (
    <div className="space-y-4" data-testid="receptions-tab">
      <div className="flex items-center justify-between">
        <p className="text-xs text-zinc-500" data-testid="receptions-count">{items.length} cereri de recepție</p>
        <div className="flex gap-2">
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-200 hover:text-zinc-100" data-testid="reception-import-pdf-btn">
          <FileUp className="h-3.5 w-3.5" /> {parsing ? "Se citește..." : "Adaugă din PDF"}
          <input type="file" accept=".pdf" className="hidden" data-testid="reception-import-pdf-input" disabled={parsing}
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) importPdf(f); }} />
        </label>
        <button onClick={() => setEditing({})} data-testid="reception-new-btn"
          className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-b from-orange-500 to-rose-500 px-3 py-1.5 text-xs font-medium text-white">
          <Plus className="h-3.5 w-3.5" /> Cerere nouă de recepție
        </button>
        </div>
      </div>
      {loading ? <p className="text-xs text-zinc-500">Se încarcă...</p> : items.length === 0 ? (
        <p className="rounded-lg border border-dashed border-zinc-800 px-4 py-6 text-center text-xs text-zinc-500" data-testid="receptions-empty">Nicio cerere de recepție încă.</p>
      ) : (
        <div className="space-y-2">
          {items.map((r) => <ReceptionRow key={r.id} r={r} onEdit={() => setEditing(r)} onDelete={() => del(r.id)} onChecked={load} />)}
        </div>
      )}
      {editing && (
        <ReceptionEditor reception={editing.id ? editing : null} draft={editing.draft} catalog={catalog}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function ReceptionRow({ r, onEdit, onDelete, onChecked }) {
  const st = STATUS[r.status] || { label: r.status, cls: "bg-zinc-800 text-zinc-300" };
  const [checking, setChecking] = useState(false);
  const bad = (r.check?.rows || []).filter((x) => !x.ok).length;
  const checkNow = async () => {
    setChecking(true);
    try {
      const res = await axios.post(`${API}/${r.id}/check`);
      toast.success(`Cererea ${r.request_number}: ${STATUS[res.data.status]?.label || res.data.status}`);
      onChecked();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setChecking(false); }
  };
  return (
    <div className="flex items-center justify-between rounded-lg border border-zinc-800 bg-zinc-950/40 p-3" data-testid={`reception-${r.id}`}>
      <div>
        <p className="text-sm font-semibold text-zinc-100">
          <PackageCheck className="mr-1.5 inline h-4 w-4 text-sky-300" />
          Cerere <span className="font-mono text-sky-300" data-testid={`reception-${r.id}-number`}>{r.request_number}</span>
        </p>
        <p className="text-[11px] text-zinc-500" data-testid={`reception-${r.id}-counts`}>
          SKU-uri: <span className="text-zinc-300">{r.total_skus}</span> · Buc: <span className="text-zinc-300">{r.total_qty}</span> · {new Date(r.created_at).toLocaleDateString("ro-RO")}
          {r.check && (
            <span className={bad ? "text-rose-300" : "text-emerald-300"} data-testid={`reception-${r.id}-check-summary`}>
              {" · "}Verificat {new Date(r.check.checked_at).toLocaleString("ro-RO")}: {bad ? `${bad} SKU cu stoc diferit` : "stocuri OK"}
            </span>
          )}
        </p>
      </div>
      <div className="flex items-center gap-3">
        {r.status === "in_receptie" && (
          <button onClick={checkNow} disabled={checking} data-testid={`reception-check-${r.id}`}
            title="Verifică acum (necesită mail-ul „Receptie finalizata” de la eMAG)"
            className="rounded border border-sky-500/40 bg-sky-500/10 px-2 py-1 text-[11px] text-sky-200 hover:bg-sky-500/20 disabled:opacity-50">
            {checking ? "..." : "Verifică acum"}
          </button>
        )}
        <span className={`rounded px-2 py-0.5 text-[10px] font-semibold ${st.cls}`} data-testid={`reception-${r.id}-status`}>{st.label}</span>
        <button onClick={onEdit} data-testid={`reception-edit-${r.id}`} className="rounded border border-zinc-700 bg-zinc-900 px-2 py-1 text-[11px] text-zinc-200 hover:text-zinc-100">Detalii</button>
        <button onClick={onDelete} data-testid={`reception-delete-${r.id}`} className="rounded border border-rose-500/40 bg-rose-500/10 p-1 text-rose-200"><Trash2 className="h-3 w-3" /></button>
      </div>
    </div>
  );
}

function ReceptionEditor({ reception, draft, catalog, onClose, onSaved }) {
  const isNew = !reception;
  const init = reception || draft;
  const [number, setNumber] = useState(init?.request_number || "");
  const [rows, setRows] = useState(init?.items?.length ? init.items : [{ sku: "", name: "", qty: 1 }]);
  const [notes, setNotes] = useState(reception?.notes || "");
  const [saving, setSaving] = useState(false);

  const setRow = (i, patch) => setRows((p) => p.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const valid = rows.filter((r) => (r.sku || "").trim() && r.qty > 0);
  const totalQty = valid.reduce((s, r) => s + r.qty, 0);

  const save = async () => {
    if (!number.trim()) { toast.error("Completează numărul cererii"); return; }
    if (!valid.length) { toast.error("Adaugă cel puțin un SKU cu cantitate"); return; }
    setSaving(true);
    try {
      const payload = { request_number: number.trim(), items: valid, notes };
      if (isNew) await axios.post(API, payload);
      else await axios.put(`${API}/${reception.id}`, payload);
      toast.success(isNew ? "Cerere creată — status: În recepție" : "Salvat");
      onSaved();
    } catch (e) { toast.error("Eroare: " + errMsg(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose} data-testid="reception-editor">
      <div className="w-full max-w-3xl rounded-xl border border-zinc-800 bg-zinc-950 p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-zinc-100">{isNew ? "Cerere nouă de recepție" : `Cerere recepție ${reception.request_number}`}</h3>
          <button onClick={onClose} data-testid="reception-editor-close" className="text-zinc-400 hover:text-zinc-100"><X className="h-4 w-4" /></button>
        </div>
        <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">Număr cerere eMAG</label>
        <input value={number} onChange={(e) => setNumber(e.target.value)} data-testid="reception-number-input" autoFocus
          className="mb-3 w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-orange-500/50" />
        <div className="mb-2 flex items-center justify-between">
          <h4 className="text-xs font-semibold uppercase text-zinc-300">Produse ({valid.length} SKU · {totalQty} buc)</h4>
          <button onClick={() => setRows((p) => [...p, { sku: "", name: "", qty: 1 }])} data-testid="reception-add-row"
            className="inline-flex items-center gap-1 rounded-lg border border-zinc-700 bg-zinc-900 px-2.5 py-1 text-[11px] text-zinc-200">
            <Plus className="h-3 w-3" /> Adaugă produs
          </button>
        </div>
        <div className="max-h-96 overflow-y-auto rounded-lg border border-zinc-800">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900 text-[10px] uppercase text-zinc-400">
              <tr><th className="px-2 py-1.5 text-left">SKU / Nume</th><th className="w-28 px-2 py-1.5 text-right">Stoc inițial FBE</th><th className="w-24 px-2 py-1.5 text-right">Cantitate</th><th className="w-8" /></tr>
            </thead>
            <tbody>
              {rows.map((it, i) => (
                <tr key={i} className="border-t border-zinc-900">
                  <td className="px-2 py-1">
                    <SkuAutocomplete value={it.sku} catalog={catalog} testId={`reception-row-${i}-sku`}
                      onPick={(m) => setRow(i, { sku: m.sku, name: m.name })} />
                    {it.name && <p className="mt-0.5 truncate text-[10px] text-zinc-500" title={it.name}>{it.name.slice(0, 80)}</p>}
                  </td>
                  <td className="px-2 py-1 text-right font-mono text-[12px] text-zinc-400" data-testid={`reception-row-${i}-initial`}>
                    {it.initial_stock ?? <span className="text-[10px] italic text-zinc-600">la salvare</span>}
                  </td>
                  <td className="px-2 py-1">
                    <input type="number" min="1" step="1" value={it.qty || ""} data-testid={`reception-row-${i}-qty`}
                      onChange={(e) => setRow(i, { qty: parseInt(e.target.value, 10) || 0 })}
                      className="w-full rounded border border-zinc-800 bg-zinc-900 px-2 py-1 text-right text-zinc-100 outline-none" />
                  </td>
                  <td className="px-2 py-1 text-right">
                    <button onClick={() => setRows((p) => p.filter((_, idx) => idx !== i))} data-testid={`reception-row-${i}-remove`}
                      className="rounded p-1 text-rose-300 hover:bg-rose-500/10"><X className="h-3 w-3" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {reception?.check && <CheckResult check={reception.check} />}
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Note (opțional)" data-testid="reception-notes"
          className="mt-3 w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none" rows="2" />
        <div className="mt-4 flex items-center justify-between">
          <span className="text-[11px] text-zinc-500">
            Status: <span className="text-sky-300">{STATUS[reception?.status || "in_receptie"]?.label}</span>
          </span>
          <div className="flex gap-2">
            <button onClick={onClose} className="rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300">Anulează</button>
            <button onClick={save} disabled={saving} data-testid="reception-save"
              className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-b from-orange-500 to-rose-500 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50">
              <Save className="h-3.5 w-3.5" /> {saving ? "..." : "Salvează cererea"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CheckResult({ check }) {
  return (
    <div className="mt-3 rounded-lg border border-zinc-800" data-testid="reception-check-result">
      <p className="bg-zinc-900 px-2 py-1.5 text-[10px] uppercase text-zinc-400">
        Verificare {new Date(check.checked_at).toLocaleString("ro-RO")} · stoc inițial − vânzări FBE + recepție = ar trebui
      </p>
      <table className="w-full text-[12px]">
        <thead className="text-[10px] uppercase text-zinc-500">
          <tr>{["SKU", "Inițial", "Vânzări", "Recepție", "Ar trebui", "Actual", ""].map((h) => <th key={h} className="px-2 py-1 text-right first:text-left">{h}</th>)}</tr>
        </thead>
        <tbody>
          {check.rows.map((x) => (
            <tr key={x.sku} className="border-t border-zinc-900 font-mono" data-testid={`check-row-${x.sku}`}>
              <td className="px-2 py-1 text-sky-300">{x.sku}</td>
              <td className="px-2 py-1 text-right">{x.initial ?? "—"}</td>
              <td className="px-2 py-1 text-right">{x.sales}</td>
              <td className="px-2 py-1 text-right">{x.qty}</td>
              <td className="px-2 py-1 text-right font-bold">{x.expected ?? "—"}</td>
              <td className="px-2 py-1 text-right font-bold">{x.current ?? "—"}</td>
              <td className={`px-2 py-1 text-right font-sans text-[10px] font-semibold ${x.ok ? "text-emerald-300" : "text-rose-300"}`}>{x.ok ? "OK" : "DIFERĂ"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
